<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { messageTimestamp } from 'shared/helpers/timeHelper';

import MessageStatus from './MessageStatus.vue';
import Icon from 'next/icon/Icon.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMessageContext } from './provider.js';

import { MESSAGE_STATUS, MESSAGE_TYPES } from './constants';

const {
  isAFacebookInbox,
  isALineChannel,
  isAPIInbox,
  isASmsInbox,
  isATelegramChannel,
  isATwilioChannel,
  isAWebWidgetInbox,
  isAWhatsAppChannel,
  isAnEmailChannel,
  isAnInstagramChannel,
  isATiktokChannel,
} = useInbox();

const {
  status,
  isPrivate,
  createdAt,
  sourceId,
  messageType,
  contentAttributes,
} = useMessageContext();

const { t } = useI18n();

// Facebook comment threads: say where the bubble lives on Facebook.
const facebookLabel = computed(() => {
  const attrs = contentAttributes.value || {};
  if (!attrs.facebookComment || attrs.deleted) return null;
  if (attrs.privateReply) {
    return {
      icon: 'i-lucide-send',
      text: t('CONVERSATION.FACEBOOK_COMMENT.LABEL.PRIVATE_REPLY'),
    };
  }
  if (attrs.facebookComment.hidden) {
    return {
      icon: 'i-lucide-eye-off',
      text: t('CONVERSATION.FACEBOOK_COMMENT.LABEL.HIDDEN'),
    };
  }
  return {
    icon: 'i-lucide-message-square',
    text: t('CONVERSATION.FACEBOOK_COMMENT.LABEL.COMMENT'),
  };
});

const linkedinComment = computed(() => {
  const attrs = contentAttributes.value || {};
  return attrs.linkedinComment && !attrs.deleted ? attrs.linkedinComment : null;
});

const readableTime = computed(() =>
  messageTimestamp(createdAt.value, 'LLL d, h:mm a')
);

const showStatusIndicator = computed(() => {
  if (isPrivate.value) return false;
  // Don't show status for failed messages, we already show error message
  if (status.value === MESSAGE_STATUS.FAILED) return false;
  // Don't show status for deleted messages
  if (contentAttributes.value?.deleted) return false;

  if (messageType.value === MESSAGE_TYPES.OUTGOING) return true;
  if (messageType.value === MESSAGE_TYPES.TEMPLATE) return true;

  return false;
});

const isSent = computed(() => {
  if (!showStatusIndicator.value) return false;

  // Messages will be marked as sent for the Email channel if they have a source ID.
  if (isAnEmailChannel.value) return !!sourceId.value;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isASmsInbox.value ||
    isATelegramChannel.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.SENT;
  }

  // API inbox messages use real sent/delivered/read status values from the external system.
  if (isAPIInbox.value) return status.value === MESSAGE_STATUS.SENT;

  // All messages will be mark as sent for the Line channel, as there is no source ID.
  if (isALineChannel.value) return true;

  return false;
});

const isDelivered = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isASmsInbox.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.DELIVERED;
  }
  // API inbox messages use real delivered status from the external system.
  if (isAPIInbox.value) return status.value === MESSAGE_STATUS.DELIVERED;
  // All messages marked as delivered for the web widget inbox once they are sent.
  if (isAWebWidgetInbox.value) {
    return status.value === MESSAGE_STATUS.SENT;
  }
  if (isALineChannel.value) {
    return status.value === MESSAGE_STATUS.DELIVERED;
  }

  return false;
});

const isRead = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.READ;
  }

  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return status.value === MESSAGE_STATUS.READ;
  }

  return false;
});

const statusToShow = computed(() => {
  if (isRead.value) return MESSAGE_STATUS.READ;
  if (isDelivered.value) return MESSAGE_STATUS.DELIVERED;
  if (isSent.value) return MESSAGE_STATUS.SENT;

  return MESSAGE_STATUS.PROGRESS;
});
</script>

<template>
  <div class="text-xs flex items-center gap-1.5">
    <div class="inline">
      <time class="inline">{{ readableTime }}</time>
    </div>
    <span v-if="facebookLabel" class="inline-flex items-center gap-0.5">
      <Icon :icon="facebookLabel.icon" class="size-3" />
      {{ facebookLabel.text }}
    </span>
    <a
      v-if="facebookLabel && contentAttributes.facebookComment.permalinkUrl"
      :href="contentAttributes.facebookComment.permalinkUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="skip-context-menu hover:underline"
    >
      {{ $t('CONVERSATION.FACEBOOK_COMMENT.VIEW_ON_FACEBOOK') }}
    </a>
    <span v-if="linkedinComment" class="inline-flex items-center gap-0.5">
      <Icon icon="i-lucide-message-square" class="size-3" />
      {{ $t('CONVERSATION.LINKEDIN_COMMENT.LABEL') }}
    </span>
    <a
      v-if="linkedinComment && linkedinComment.permalinkUrl"
      :href="linkedinComment.permalinkUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="skip-context-menu hover:underline"
    >
      {{ $t('CONVERSATION.LINKEDIN_COMMENT.VIEW_ON_LINKEDIN') }}
    </a>
    <Icon v-if="isPrivate" icon="i-lucide-lock-keyhole" class="size-3" />
    <MessageStatus v-if="showStatusIndicator" :status="statusToShow" />
  </div>
</template>
`
